/**
 * The organisation's brand details on printed sheets (brand kit): the logo
 * and the contact lines `{org.address}`, `{org.phone}`, … Which ones a
 * template uses, and which of those are still empty — the template editor
 * needs them all, an export only the ones its template prints.
 */

import { forEachElement, tokenizePlaceholders, type PrintTemplateSpec } from 'shared-types';
import { t } from '../../potree/translation/markers';
import type { TemplateLibraryConfigFieldsFragment } from '../../types/graphqlTypes';

/** The brand kit's text details (the `{org.<key>}` placeholders besides the organisation name). */
export const BRAND_TEXT_DETAILS = ['address', 'phone', 'email', 'website', 'vat'] as const;
export type BrandTextDetail = (typeof BRAND_TEXT_DETAILS)[number];
export type BrandDetail = 'logo' | BrandTextDetail;
export const BRAND_DETAILS: readonly BrandDetail[] = ['logo', ...BRAND_TEXT_DETAILS];

export const BRAND_FIELDS: { key: BrandTextDetail; label: string; multiline?: boolean }[] = [
  { key: 'address', label: t('address'), multiline: true },
  { key: 'phone', label: t('phone') },
  { key: 'email', label: t('email') },
  { key: 'website', label: t('website') },
  { key: 'vat', label: t('VAT number') },
];

const isBrandTextDetail = (key: string): key is BrandTextDetail => (BRAND_TEXT_DETAILS as readonly string[]).includes(key);

/** The brand details a template prints: `{org.*}` placeholders anywhere in its text, and the logo where an image shows it. */
export function brandDetailsUsedBy(spec: PrintTemplateSpec): BrandDetail[] {
  const used = new Set<BrandDetail>();
  const scan = (text: string) => {
    for (const token of tokenizePlaceholders(text)) {
      if (token.kind !== 'placeholder' || !token.key.startsWith('org.')) continue;
      const key = token.key.slice(4);
      if (isBrandTextDetail(key)) used.add(key);
    }
  };
  for (const field of spec.fields) scan(field.defaultValue);
  for (const page of spec.pages) {
    forEachElement(page.elements, (element) => {
      switch (element.kind) {
        case 'text':
          scan(element.content);
          break;
        case 'image':
          if (element.source.kind === 'orgLogo') used.add('logo');
          break;
        case 'table':
          for (const column of element.columns) {
            scan(column.header);
            scan(column.cell);
          }
          if (element.rows.kind === 'static') for (const row of element.rows.cells) row.forEach(scan);
          break;
        case 'legend':
          scan(element.title);
          for (const item of element.items) scan(item.label);
          break;
        default:
          break;
      }
    });
  }
  return BRAND_DETAILS.filter((detail) => used.has(detail));
}

/** Of `details`, the ones the brand kit has no value for yet. */
export function missingBrandDetails(config: Pick<TemplateLibraryConfigFieldsFragment, 'brand' | 'logo'>, details: readonly BrandDetail[]): BrandDetail[] {
  return details.filter((detail) => (detail === 'logo' ? !config.logo : !(config.brand[detail] ?? '').trim()));
}
